import { useSignal } from '@preact/signals'

import {
	Modal,
	Typography,
	CheckIcon,
	CloseXIcon,
	TextLink,
	LoadingIcon
} from 'components'

type Props = {
	closeModal: () => void
	handleAcceptInvitation: (projectId: string) => void
	handleRejectInvitation: (projectId: string) => void
	isModalOpen: boolean
	projects: string[]
}

const PendingInvitationsModal = ({
	closeModal,
	handleAcceptInvitation,
	handleRejectInvitation,
	isModalOpen,
	projects
}: Props) => {
	const invitationIndex = useSignal<number | null>(null)
	const isInvitationInProgress = typeof invitationIndex.value === 'number'

	const onAcceptInvitation = async (index: number, projectId: string) => {
		invitationIndex.value = index
		await handleAcceptInvitation(projectId)
		invitationIndex.value = null
	}

	const handleCloseModal = () => {
		if (isInvitationInProgress) return
		closeModal()
	}

	return (
		<Modal isOpen={isModalOpen} onCloseModal={handleCloseModal}>
			<div class='mb-10 px-10 pt-10'>
				<Typography
					color='text-product'
					text='You have pending invitations'
					variant='h2'
				/>
				<Typography
					class='mt-5'
					color='text-black-200'
					text='Click on a project to accept the invitation'
					variant='text2'
				/>

				<ul class='mb-10 mt-10 space-y-5'>
					{projects.map((projectId, index) => (
						<li
							class='hover:bg-black-700 group relative flex w-full items-center justify-between rounded-md bg-black-800 p-5 transition'
							key={projectId}
						>
							<Typography
								class='cursor-pointer truncate pr-6'
								color='text-black-200 group-hover:text-black-100'
								text={projectId}
								variant='label1'
								weight='font-normal'
							/>

							{index === invitationIndex.value && (
								<LoadingIcon class='h-[0.875rem] w-[0.875rem] text-product' />
							)}

							{!isInvitationInProgress && (
								<div class='hidden gap-5 group-hover:flex'>
									<button
										aria-label={`Accept invitation for ${projectId}`}
										class='z-10 h-[0.875rem] w-[0.875rem] text-product transition-opacity duration-100'
										onClick={() => {
											onAcceptInvitation(index, projectId)
										}}
										type='button'
									>
										<CheckIcon className='h-full w-full' />
									</button>
									<button
										aria-label={`Reject invitation for ${projectId}`}
										class='z-10 h-[0.875rem] w-[0.875rem] text-error transition-opacity duration-100'
										onClick={() => {
											handleRejectInvitation(projectId)
										}}
										type='button'
									>
										<CloseXIcon />
									</button>
								</div>
							)}
						</li>
					))}
				</ul>

				<div class='mt-5 flex items-center justify-between'>
					<TextLink
						ariaLabel='Close modal'
						disabled={isInvitationInProgress}
						onClick={handleCloseModal}
						size='small'
						text='Cancel'
						variant='secondary'
					/>
				</div>
			</div>
		</Modal>
	)
}

export { PendingInvitationsModal }
